import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { HotelConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { LastSearchData, HotelLastSearchData } from "../../LastSearchData";
import { setCookie } from "src/components/utility/cookie";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Logger } from "bernie-logger";
import { travelersStateOnConfig } from "../global/travelers/travelersState";
import { TravelersState } from "stores/wizard/state/global";
import { HotelRatesType } from "stores/wizard/state/typings";

export interface ToggleCheckbox {
  stateToForce?: boolean;
}

export class HotelWizardState implements LOBState {
  logger: Logger;

  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  public lastSearchData?: HotelLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: HotelConfig;

  @observable
  public subLOBState = "";

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public numberOfErrors = 0;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @computed
  public get date() {
    return this.globalState.date;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @observable
  public hotelTravelers: TravelersState;

  @computed
  public get travelers() {
    return this.hotelTravelers;
  }

  @observable
  public hotelTabSelected = true;

  @action
  public toggleTabSelected = () => {
    this.hotelTabSelected = !this.hotelTabSelected;
  };

  @observable
  public isMultiplePlacesChecked = false;

  @action
  public toggleMultiplePlacesCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isMultiplePlacesChecked = stateToForce ?? !this.isMultiplePlacesChecked;
  };

  @observable
  public isAddAFlightChecked = false;

  @action
  public toggleAddAFlightCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isAddAFlightChecked = stateToForce ?? !this.isAddAFlightChecked;
  };

  @observable
  public isAddACarChecked = false;

  @action
  public toggleAddACarCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isAddACarChecked = stateToForce ?? !this.isAddACarChecked;
  };

  @observable
  public hotelTypeSubTab: HotelRatesType = HotelRatesType.STANDALONE;

  @action
  public updateHotelTypeSubTab(hotelTypeSubTab: HotelRatesType) {
    this.cleanErrorState();
    this.hotelTypeSubTab = hotelTypeSubTab;
  }

  @observable
  public isPackageRatesTermsChecked = false;

  @action
  public togglePackageRatesTermsCheckbox = ({ stateToForce }: ToggleCheckbox = {}) => {
    this.isPackageRatesTermsChecked = stateToForce ?? !this.isPackageRatesTermsChecked;
  };

  @computed
  public get hotelPackageType() {
    if (this.isAddAFlightChecked && this.isAddACarChecked) {
      return "fhc";
    }
    if (this.isAddAFlightChecked) {
      return "fh";
    }
    if (this.isAddACarChecked) {
      return "hc";
    }

    return "";
  }

  @observable
  public regionId: string;

  @action
  public setRegionId(regionId: string) {
    this.regionId = regionId;
  }

  @computed
  public get hotelRegionId() {
    return this.regionId;
  }

  @observable
  public htgStartDate = "";

  @computed
  public get startDate() {
    return this.htgStartDate;
  }

  @action
  public setHtgStartDate(htgStartDate: string) {
    this.htgStartDate = htgStartDate;
  }

  @observable
  public htgEndDate = "";

  @computed
  public get endDate() {
    return this.htgEndDate;
  }

  @action
  public setHtgEndDate(htgEndDate: string) {
    this.htgEndDate = htgEndDate;
  }

  @computed
  public get formAction() {
    if (this.hotelTypeSubTab !== HotelRatesType.PACKAGE_RATES && (this.isAddAFlightChecked || this.isAddACarChecked)) {
      return this.config.form.actionPackage;
    }

    return this.config.form.action;
  }

  // validations
  @observable
  public destinationInvalidKey: string;

  @observable
  public originInvalidKey: string;

  @observable
  public hotelTravelersInvalidKey = "";

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("destination")!;

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @action
  public validateLessThanNTravelers = () => {
    const isValid = this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers);
    if (!isValid) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @observable
  public advancedOptionsFilterVisible = true;

  @observable
  public checkboxFilterChecks = {
    businessFriendly: false,
    familyFriendly: false,
    freeCancellation: false,
    breakfastIncluded: false,
  };

  @action
  public validateMaxDateRange = () => {
    if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")! || this.findFocusElement("endDate")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
  };

  public validateForm = () => {
    this.cleanErrorState();
    const validations: boolean[] = [
      this.validateDestinationField(),
      this.validateEndDateField(),
      this.validateTravelersField(),
    ];

    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void) {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @observable
  public sourceIdSelected = "";

  @observable
  public latLong = "";

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    const { data } = (selection as TypeaheadSelectionProps) || {};
    const { selected, location } = data || "";
    const { lat, long } = location || "";
    this.sourceIdSelected = selected;
    this.latLong = lat && long ? `${lat},${long}` : "";
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    this.originInvalidKey = "";
  };

  @action
  public changeFormFilterProperties = (event: React.ChangeEvent<HTMLFormElement>) => {
    const { key: filterKey, checked: isChecked, name: filterName, value: filterValue } = event.currentTarget;
    this.checkboxFilterChecks[filterKey] = isChecked;

    const trackingInfo: FlexTrackingInfo = {
      moduleName: "HOT.SF",
      rfrr: `${filterName}.${filterValue}.${isChecked}`,
      action: Action.CLICK,
      linkName: `${filterName}.${filterValue}`,
    };

    sendDelayedTrackEvent(trackingInfo, this.config.analytics);
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers, this.travelers);

    if (this.lastSearchData && this.lastSearchData.travelers && this.validateTravelersField()) {
      this.lastSearchData.travelers = JSON.stringify(this.travelers);
    }

    this.hotelTravelersInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string,
    timestamp?: number
  ) {
    this.globalState.updateDestinationFromCookie(
      value,
      regionID,
      countryCode,
      latLong,
      ttla,
      destinationID,
      hotelID,
      timestamp
    );
    this.destinationInvalidKey = "";
  }

  public updateDestinationFromContext(context: ExtendedContextStore) {
    const location = context?.searchContext?.location;

    if (location?.extendedName) {
      this.updateDestinationFromCookie(
        location.extendedName,
        location.id || "",
        "",
        location.geoCoordinate ? `${location.geoCoordinate.latitude},${location.geoCoordinate.longitude}` : "",
        location.code || "",
        "",
        ""
      );
    }
  }

  @action
  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    timestamp: number
  ) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla, timestamp);
    this.originInvalidKey = "";
  }

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const destinationValue = lastSearchData.destination?.value
        ? decodeURIComponent(lastSearchData.destination?.value)
        : "";
      const originValue = lastSearchData.flight_origin?.value
        ? decodeURIComponent(lastSearchData.flight_origin?.value)
        : "";

      const startDateFromCookie = lastSearchData.date?.start ? new Date(lastSearchData.date.start) : new Date();
      const endDateFromCookie = lastSearchData.date?.end ? new Date(lastSearchData.date.end) : new Date();

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateDateSelection(startDateToUse, endDateToUse);

      const travelers: TravelersState = JSON.parse(lastSearchData.travelers);
      const hotelTravelers = travelers.hotel ?? this.travelers.hotel;

      this.updateHotelTravelersSelection(hotelTravelers);

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
        lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
        lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
        lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
        lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
        lastSearchData.destination?.hotelID ? lastSearchData.destination.hotelID : "",
        lastSearchData.timestamp
      );

      const lastSearchCheckCarValue = lastSearchData.addACar
        ? JSON.parse(lastSearchData.addACar)
        : this.isAddACarChecked;
      const lastSearchCheckMultiplePlacesValue = lastSearchData.multiplePlacesToStay
        ? JSON.parse(lastSearchData.multiplePlacesToStay)
        : this.isMultiplePlacesChecked;
      const lastSearchCheckFlightValue = lastSearchData.addAFlight
        ? JSON.parse(lastSearchData.addAFlight)
        : this.isAddAFlightChecked;

      if (lastSearchCheckCarValue) {
        this.toggleAddACarCheckbox();
      }

      if (lastSearchCheckMultiplePlacesValue) {
        this.toggleMultiplePlacesCheckbox();
      }

      if (lastSearchCheckFlightValue) {
        this.toggleAddAFlightCheckbox();
      }

      if (this.isAddAFlightChecked) {
        this.updateOriginFromCookie(
          originValue && originValue !== "undefined" ? originValue : "",
          lastSearchData.flight_origin?.regionID ? lastSearchData.flight_origin.regionID : "",
          lastSearchData.flight_origin?.countryCode ? lastSearchData.flight_origin.countryCode : "",
          lastSearchData.flight_origin?.latLong ? lastSearchData.flight_origin.latLong : "",
          lastSearchData.flight_origin?.ttla ? lastSearchData.flight_origin.ttla : "",
          lastSearchData.timestamp
        );
      }
    }
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const date = this.date;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        hotel: {
          destination: {
            value: destination.value || "",
            regionID: destinationMetaData.regionId || "",
            ttla: destinationMetaData.ttla || "",
            countryCode: destinationMetaData.countryCode || "",
            latLong: destinationMetaData.latLong || "",
            destinationID: destinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          travelers: JSON.stringify(this.travelers),
          infantSeating: this.travelers.hotel.infantSeating,
          multiplePlacesToStay: this.isMultiplePlacesChecked.toString(),
          addACar: this.isAddACarChecked.toString(),
          addAFlight: this.isAddAFlightChecked.toString(),
          timestamp: Math.floor(Date.now() / 1000),
        },
      };

      //only add flight last search data to the cookie if needed
      if (this.isAddAFlightChecked) {
        lastSearchCookieObject.hotel!.flight_origin = {};
        lastSearchCookieObject.hotel!.flight_origin.value = this.isAddAFlightChecked ? this.location.origin.value : "";
        lastSearchCookieObject.hotel!.flight_origin.regionID = this.isAddAFlightChecked
          ? this.location.origin.metaData.regionId
          : "";
        lastSearchCookieObject.hotel!.flight_origin.ttla = this.isAddAFlightChecked
          ? this.location.origin.metaData.ttla
          : "";
        lastSearchCookieObject.hotel!.flight_origin.countryCode = this.isAddAFlightChecked
          ? this.location.origin.metaData.countryCode
          : "";
        lastSearchCookieObject.hotel!.flight_origin.latLong = this.isAddAFlightChecked
          ? this.location.origin.metaData.latLong
          : "";
      }

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
        days: 30,
      });
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: HotelConfig, globalState: GlobalWizardState, logger: Logger, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    this.logger = logger;
    this.hotelTravelers = travelersStateOnConfig(this.config.travelers);
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.hotel;
    }
  }
}
